import React from 'react'
import FuboSportsTemplate from 'src/components/template/fubosports'
import { addOnCodes, devicePosters, leagueIds, ratePlanCodes, sportIds } from 'src/constants'
import { usPlanData } from 'src/constants/planData/usPlanData'
import { useIsFuboSportsAvailable } from 'src/modules/hooks/useIsFuboSportsAvailable'
import { commonCtaText, defaultCTAURI } from 'src/modules/utils/constants'
import styled from 'styled-components'
import { videos } from 'src/constants/videos'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { body } from 'src/constants/marketing-ds'
import NHL from '../nhl'

export default (props) => {
  const { isAvailable } = useIsFuboSportsAvailable()
  const commonProps = {
    location: props.location,
    lang: 'en',
    market: 'us',
    video: videos.nhl.videoUrl,
    staticAnnotation: videos.nhl.staticAnnotation,
    title: (
      <>
        Watch <span className="no-wrap">out-of-market</span> NHL games
      </>
    ),
    subtitle: (
      <>
        Stream 1,000+ out-of-market games with ESPN Unlimited.{' '}
        <span className="desktop-line-break">Included FREE with Fubo Sports.</span>
      </>
    ),
  }

  if (isAvailable === false) {
    return (
      <NHL
        offerBanner={{
          title: ['Fubo Sports is unavailable in your area.', 'Please see our other plans.'],
          showIcon: false,
        }}
      />
    )
  }

  return (
    <StyledContainer>
      <FuboSportsTemplate
        {...commonProps}
        ctaText={commonCtaText.startWatchingForFree}
        offerCode={ratePlanCodes.fubo_sports_monthly}
        channelsAddonCode={addOnCodes.fubo_sports_redzone}
        hasPlanCards={true}
        networkHeadline={
          <>
            Watch hockey and 25+ live channels{' '}
            <span className="desktop-line-break">including ESPN Unlimited</span>
          </>
        }
        plans={[
          {
            ...usPlanData.fubo_sports_monthly,
            banner: null,
            subtitle: 'Hockey & more sports for less',
            listItems: () => [
              { available: true, text: '25+ channels for sports fans' },
              { available: true, text: 'ESPN Unlimited included' },
              { available: true, text: 'NHL, NFL, MLB, NBA' },
              { available: true, text: 'Stream on 10 screens at once' },
              { available: true, text: 'ABC, CBS, FOX' },
              // { available: false, text: 'NBC' },
            ],
            footnoteInCard: (data) => {
              let ctaURL = replaceUrlParam(
                defaultCTAURI,
                'offer',
                ratePlanCodes.fubo_sports_monthly
              )
              ctaURL = replaceUrlParam(ctaURL, 'addons[]', addOnCodes.fubo_sports_redzone)
              ctaURL = replaceUrlParam(ctaURL, 'comp', 'plan-card-footnote')

              return (
                <a
                  style={{ textDecoration: 'underline', ...body.sm, padding: '10px' }}
                  href={ctaURL}
                >
                  Add NHL Network for $9.99/mo
                </a>
              )
            },
          },
        ]}
        networkSet={'fubo_sports_hockey'}
        sportId={sportIds.Hockey}
        leagueId={leagueIds.NHL}
        matchtickerSportId={sportIds.Hockey}
        matchtickerLeagueName="NHL"
        matchtickerLeagueId={leagueIds.NHL}
        disableAnchor={true}
        platformImage={devicePosters.nhl}
        faqListOverride={overrideFAQs}
      />
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  .content-container .hero-content {
    max-width: 680px;
  }
`

const overrideFAQs = [
  {
    question: 'What is Fubo Sports?',
    answer:
      'Fubo Sports is Fubo’s newest streaming option for sports fans who want more sports channels for less money. Watch NHL, NFL, college sports and more starting at just $45.99/month the first month, then $55.99/mo. No cable required, no contracts.',
  },
  {
    question: 'Can I watch out-of-market NHL games with Fubo Sports?',
    answer:
      'Yes! With Fubo Sports, you can watch out-of-market NHL games with ESPN Unlimited, right in the Fubo app. The best part is that it’s included FREE. Fubo Sports also includes a variety of regional sports networks for in-market NHL coverage — including the Portland Trail Blazers, Utah Jazz, Vegas Golden Knights, Florida Panthers, Tampa Bay Lightning, Utah Mammoth and others. Regional sports network access is dependent on your zip code.',
  },
  {
    question: 'Which channels are included with Fubo Sports?',
    answer:
      'Fubo Sports includes 20+ sports channels, including ABC (select markets), CBS, FOX, FS1, ESPN, ESPN Unlimited (previously ESPN+), NFL Network, SEC Network, ACC Network, Big Ten Network, Tennis Channel and more. Other sports channels like NFL RedZone are available as add-ons. Plus regional sports networks may be included if they are available in your area.',
  },
  {
    question: 'Is there a free trial for Fubo Sports?',
    answer:
      'Yes! You can try Fubo Sports for free and start streaming live games right away. Cancel anytime.',
  },
  {
    question: 'Is there a discount for the first month?',
    answer:
      'Absolutely. Your first month of Fubo Sports is only $45.99 instead of the regular price of $55.99 per month. Sign up now to save $10 off the regular monthly price.',
  },
  {
    question: 'Can I add more sports channels to Fubo Sports?',
    answer:
      'Yes! You can customize your plan with add-ons that include NHL Network, NFL RedZone, MLB Network and other sports channels.',
  },
  {
    question: 'What devices can I use to watch Fubo Sports?',
    answer:
      "Stream on all your devices. Download the free Fubo app for your smart TV, Roku, Apple TV, phone, tablet and more. Or simply log into fubo.tv on your computer. Whether you're home or on the go, you're always in the game.",
  },
  {
    question: 'Does Fubo Sports include local channels?',
    answer:
      'Yes! Fubo Sports has local channels. They may be available in your base plan, where available, depending on your home zip code. Please refer to the channel lineup in the signup process.',
  },
  {
    question: 'What sports can I watch with Fubo Sports?',
    answer:
      'Fubo Sports has great NFL coverage plus college football, MLB, NBA, NHL, WNBA, tennis and more. As well as major national and international games, analysis and more.',
  },
  {
    question: 'Can I record games with Fubo Sports?',
    answer:
      'Yes, Fubo Sports includes Unlimited Cloud DVR at no extra cost. You can record entire games, set series recordings, and never miss a moment.',
  },
  {
    question: 'How is Fubo Sports different from other Fubo streaming TV options?',
    answer:
      'Fubo Sports is different from Fubo’s other streaming options in a few major ways. First, Fubo Sports is designed for serious sports fans only. It focuses on sports channels and doesn’t include many of the entertainment channels you’ll find in Fubo Pro and other Fubo TV plans. Fubo Sports also costs less — it’s just $45.99 for the first month, then $55.99 per month after that.',
  },
  {
    question: 'How do I sign up for Fubo Sports?',
    answer: 'Just click any of the buttons on this page to start watching for free.',
  },
]
